import React, { useState, useEffect } from "react";
import {
  Grid,
  MenuItem,
  Paper,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import PrimaryButton from "../buttons/primaryButton";
import Slider from "@mui/material/Slider";
import SecondaryButton from "../buttons/secondaryButton";
import makeStyles from "@mui/styles/makeStyles";
import CustomSlider from "./customSlider";
import { useGA4React } from "ga-4-react";
import { useSelector } from "react-redux";

const useStyles = makeStyles((theme) => ({
  slider: {
    markLabel: {
      display: "none",
    },
  },
}));

const growthMarks = [
  {
    value: 0,
    label: "All",
  },
  {
    value: 20,
    label: "Conservative",
  },
  {
    value: 40,
    label: "",
  },
  {
    value: 60,
    label: "Moderate",
  },
  {
    value: 80,
    label: "",
  },
  {
    value: 100,
    label: "Aggressive",
  },
];

const timeMarks = [
  {
    value: 0,
    label: "All",
  },
  {
    value: 25,
    label: "1-2",
  },
  {
    value: 50,
    label: "1-3",
  },
  {
    value: 75,
    label: "5+",
  },
  {
    value: 100,
    label: "7+",
  },
];

const assetHoldingsMarks = [
  {
    value: 0,
    label: "All",
  },
  {
    value: 20,
    label: "Money Market",
  },
  {
    value: 40,
    label: "Fixed Income",
  },
  {
    value: 60,
    label: "Property",
  },
  {
    value: 80,
    label: "Multi Asset",
  },
  {
    value: 100,
    label: "Equity",
  },
];

const assetAllocationMarks = [
  {
    value: 0,
    label: "All",
  },
  {
    value: 33,
    label: "Local",
  },
  {
    value: 66,
    label: "Offshore",
  },
  {
    value: 100,
    label: "Local and Offshore",
  },
];

function FundFilterer({ onFilterChange, ...props }) {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [filter, setFilter] = useState({
    category: "all",
  });

  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  let ga4React = useGA4React();
  useEffect(() => {
    onFilterChange(filter);
  }, [filter]);

  const handleSelect = (category) => {
    setSelectedCategory(category);

    if (ga4React && category) {
      ga4React.event(
        "selected_fund_filter",
        `${category}`,
        "important_for_the_right_fund"
      );
    }

    setFilter({
      category,
      [category]: "All",
    });
  };

  const handleMobileSelectChange = (event) => {
    const category = event.target.value;
    setSelectedCategory(category);

    if (ga4React && category) {
      ga4React.event(
        "selected_fund_filter",
        `${category}`,
        "important_for_the_right_fund"
      );
    }

    setFilter({
      category,
      [category]: "All",
    });
  };

  const handleSliderChange = (event, value, category) => {
    if (category === "growth") {
      value = getGrowthValue(value);
    } else if (category === "time") {
      value = getTimeValue(value);
    } else if (category === "assetHoldings") {
      value = getAssetHoldingsValue(value);
    } else if (category === "assetAllocation") {
      value = getAssetAllocationValue(value);
    }

    setFilter({
      ...filter,
      [category]: value,
    });

    onFilterChange(filter);
  };

  const getGrowthValue = (value) => {
    if (value === 0) {
      return "All";
    } else if (value === 20) {
      return "Conservative";
    } else if (value === 40) {
      return "Moderately Conservative";
    } else if (value === 60) {
      return "Moderate";
    } else if (value === 80) {
      return "Moderately Aggressive";
    } else if (value === 100) {
      return "Aggressive";
    }
  };

  const getTimeValue = (value) => {
    return timeMarks.find((mark) => mark.value === value).label + " years";
  };

  const getAssetHoldingsValue = (value) => {
    return assetHoldingsMarks.find((mark) => mark.value === value).label;
  };

  const getAssetAllocationValue = (value) => {
    return assetAllocationMarks.find((mark) => mark.value === value).label;
  };

  return (
    <Paper sx={{ p: 2, mt: 3, mb: 2 }}>
      <Typography variant={"h4"}>
        What is most important to you when looking for the right fund?
      </Typography>

      {!isMobile && (
        <Box
          dsiplay={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          spacing={{ xs: 0, md: 2 }}
        >
          <SecondaryButton
            onClick={() => handleSelect("growth")}
            color={selectedCategory === "growth" ? "primary" : "secondary"}
          >
            {"Growth & Risk"}
          </SecondaryButton>

          <SecondaryButton
            onClick={() => handleSelect("time")}
            color={selectedCategory === "time" ? "primary" : "secondary"}
          >
            {"Time"}
          </SecondaryButton>

          <SecondaryButton
            onClick={() => handleSelect("assetHoldings")}
            color={
              selectedCategory === "assetHoldings" ? "primary" : "secondary"
            }
          >
            {"Asset Holdings"}
          </SecondaryButton>

          <SecondaryButton
            onClick={() => handleSelect("assetAllocation")}
            color={
              selectedCategory === "assetAllocation" ? "primary" : "secondary"
            }
          >
            {"Region"}
          </SecondaryButton>

          <SecondaryButton
            onClick={() => handleSelect("all")}
            color={"secondary"}
            variant="outlined"
          >
            {"Show All"}
          </SecondaryButton>
        </Box>
      )}

      {isMobile && (
        <Select
          defaultValue={"all"}
          value={filter.category ? filter.category : "all"}
          sx={{ width: "100%", mt: 2 }}
          onChange={(event) => handleMobileSelectChange(event)}
        >
          <MenuItem value={"all"}>All</MenuItem>
          <MenuItem value={"growth"}>Growth</MenuItem>
          <MenuItem value={"time"}>Time</MenuItem>
          <MenuItem value={"assetHoldings"}>Asset Holdings</MenuItem>
          <MenuItem value={"assetAllocation"}>Region</MenuItem>
        </Select>
      )}

      {selectedCategory === "growth" && (
        <CustomSlider
          leftTitle={"Lower Risk"}
          leftSubtitle={"potentially lower returns"}
          rightTitle={"Higher Risk"}
          rightSubtitle={"potentially higher returns"}
          marks={growthMarks}
          currentLabel={filter.growth ? filter.growth : "All"}
          onChange={(event, value) =>
            handleSliderChange(event, value, "growth")
          }
          defaultValue={filter.growth ? filter.growth : "All"}
        />
      )}

      {selectedCategory === "time" && (
        <CustomSlider
          leftTitle={"Shorter Period"}
          leftSubtitle={"ideal for lower risk"}
          rightTitle={"Longer Period"}
          rightSubtitle={"ideal for higher risk"}
          marks={timeMarks}
          currentLabel={filter.time ? filter.time : "All"}
          onChange={(event, value) => handleSliderChange(event, value, "time")}
          defaultValue={filter.time ? filter.time : "All"}
        />
      )}

      {selectedCategory === "assetHoldings" && (
        <CustomSlider
          leftTitle={""}
          leftSubtitle={""}
          rightTitle={""}
          rightSubtitle={""}
          marks={assetHoldingsMarks}
          currentLabel={filter.assetHoldings ? filter.assetHoldings : "All"}
          onChange={(event, value) =>
            handleSliderChange(event, value, "assetHoldings")
          }
          defaultValue={filter.assetHoldings ? filter.assetHoldings : "All"}
        />
      )}

      {selectedCategory === "assetAllocation" && (
        <CustomSlider
          leftTitle={""}
          leftSubtitle={""}
          rightTitle={""}
          rightSubtitle={""}
          marks={assetAllocationMarks}
          currentLabel={filter.assetAllocation ? filter.assetAllocation : "All"}
          onChange={(event, value) =>
            handleSliderChange(event, value, "assetAllocation")
          }
          defaultValue={filter.assetAllocation ? filter.assetAllocation : "All"}
        />
      )}
    </Paper>
  );
}

export default FundFilterer;
